import { useFormContext } from 'react-hook-form';
import { Bill } from '@mews-commander/data-access';
import { Highlight, Body, Stack, StackItem } from '@optimus-web/core';
import { DataTestId, SearchType, TextKey } from '@mews-commander/enums';
import { LocalizedText } from '@mews/framework';
import { getCustomerOrCompanyFullName } from '@mews-commander/legacy-entities/src/ducks/customers';
import { useCommanderSelector } from '@mews-commander/utils';
import { SearchHookField } from '../../../components/form/hookFields';
import { AssociateOwnerCheckbox } from '../components';
import { useGetAccountName } from '../../accounts';
import { CHECKBOX_FIELD_NAME, SEARCH_FIELD_NAME, defaultValues } from './constants';
import { ChangeBillOwnerFormValues } from './types';

const searchTypes = [SearchType.Customer, SearchType.Company];

export const ChangeBillOwnerDialogContent = ({ bill }: { bill: Bill }) => {
    const { watch, setValue, reset } = useFormContext<ChangeBillOwnerFormValues>();

    const selectedOwnerId = watch(SEARCH_FIELD_NAME);
    const associateCurrentOwner = watch(CHECKBOX_FIELD_NAME);
    const currentOwnerName =
        useCommanderSelector((state) => getCustomerOrCompanyFullName(state, { id: bill.ownerId })) ?? '';
    const disableCheckbox = bill.associatedAccountId === selectedOwnerId;

    const { data } = useGetAccountName(selectedOwnerId);

    const handleCheck = () => {
        setValue(CHECKBOX_FIELD_NAME, !associateCurrentOwner);
    };

    return (
        <Stack spacing="200" vertical>
            <SearchHookField
                name={SEARCH_FIELD_NAME}
                searchTypes={searchTypes}
                onResultClear={() => {
                    reset(defaultValues);
                }}
                data-test-id={DataTestId.BillingScreenAccountSearch}
                label={<LocalizedText textKey={TextKey.SearchForAnotherCustomerOrCompany} />}
                placeholder={<LocalizedText textKey={TextKey.SearchByName} />}
                bottomCaption={
                    <LocalizedText
                        textKey={TextKey.OwnedBillAccountSearchCaption}
                        substitutes={{ ownerName: currentOwnerName }}
                    />
                }
                except={[bill.ownerId]}
                required
                newEntity
                autoFocus
            />
            {selectedOwnerId ? (
                <Stack vertical spacing="200">
                    <StackItem>
                        <Body size="small">
                            <LocalizedText textKey={TextKey.OwnedBillNewBillOwnerLabel} />
                        </Body>
                        <Highlight size="small">{data}</Highlight>
                    </StackItem>
                    <Stack vertical>
                        <AssociateOwnerCheckbox
                            name={CHECKBOX_FIELD_NAME}
                            onChange={handleCheck}
                            ownerName={currentOwnerName ?? ''}
                            disabled={disableCheckbox}
                            checked={associateCurrentOwner}
                        />
                    </Stack>
                </Stack>
            ) : null}
        </Stack>
    );
};
